"use client";

import { useState, useRef } from "react";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import {
  X, GripVertical, MapPin, Search, Plus, Trash2, Car, Footprints,
  Bike, Bus, Save, Link2, Download, ChevronDown, ChevronUp,
  CheckCircle2, Navigation, ImageOff, Check, Route, Clock, Loader2, Play, BookmarkPlus, Settings2,
} from "lucide-react";
import { Waypoint, SavedRoute, TravelMode } from "./types";
import { PanoramaFeature } from "@/api-client";
import { DragHandle, WaypointRow } from "@/features/gpxroute/WaypointList";
import TourRouteSettings from "@/features/tourroute/TourRouteSettings";
import type { TourRouteSettings as TourRouteSettingsType } from "@/features/tourroute/types";
import { useTheme } from "@/features/theme";

interface RouteBuilderPanelProps {
  waypoints: Waypoint[];
  routeName: string;
  travelMode: TravelMode;
  savedRoutes: SavedRoute[];
  isAddingMode: boolean;
  searchQuery: string;
  shareCopied: boolean;
  savedFlash: boolean;
  routeInfo?: { distanceKm: number; durationSec: number } | null;
  routeLoading?: boolean;
  onSetRouteName: (v: string) => void;
  onReorder: (v: Waypoint[]) => void;
  onRemoveWaypoint: (panoId: string) => void;
  onSetTravelMode: (v: TravelMode) => void;
  onSetAddingMode: (v: boolean) => void;
  onSetSearchQuery: (v: string) => void;
  onSave: () => void;
  onLoadRoute: (r: SavedRoute) => void;
  onDeleteRoute: (id: string) => void;
  onOpenInMaps: (app: "google" | "waze" | "apple") => void;
  onCopyShareLink: () => void;
  onExportGPX: () => void;
  allPanoramas: PanoramaFeature[];
  onAddWaypoint: (f: PanoramaFeature) => void;
  onClose: () => void;
  isDark: boolean;
  isMobile?: boolean;
  onPlayTourRoute?: () => void;
  onCreateTour?: () => void;
  tourCreating?: boolean;
  tourSettings?: TourRouteSettingsType;
  onTourSettingsChange?: (s: Partial<TourRouteSettingsType>) => void;
}

const TRAVEL_MODES: { mode: TravelMode; Icon: React.ElementType; label: string }[] = [
  { mode: "driving",   Icon: Car,        label: "Drive"   },
  { mode: "walking",   Icon: Footprints, label: "Walk"    },
  { mode: "bicycling", Icon: Bike,       label: "Bike"    },
  { mode: "transit",   Icon: Bus,        label: "Transit" },
];

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371, toRad = (d: number) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function routeStats(waypoints: Waypoint[]) {
  let km = 0;
  for (let i = 0; i < waypoints.length - 1; i++)
    km += haversineKm(waypoints[i].lat, waypoints[i].lng, waypoints[i + 1].lat, waypoints[i + 1].lng);
  const mins = Math.round(km / 50 * 60);
  const distLabel = km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
  const timeLabel = mins < 60 ? `~${mins} min` : `~${Math.floor(mins / 60)}h ${mins % 60 ? `${mins % 60}m` : ""}`;
  return { distLabel, timeLabel };
}


export default function RouteBuilderPanel({
  waypoints, routeName, travelMode, savedRoutes,
  isAddingMode, searchQuery, shareCopied, savedFlash,
  onSetRouteName, onReorder, onRemoveWaypoint,
  onSetTravelMode, onSetAddingMode, onSetSearchQuery,
  onSave, onLoadRoute, onDeleteRoute,
  onOpenInMaps, onCopyShareLink, onExportGPX,
  allPanoramas, onAddWaypoint,
  onClose, isDark: isDarkProp, isMobile,
  routeInfo, routeLoading, onPlayTourRoute,
  onCreateTour, tourCreating,
  tourSettings, onTourSettingsChange,
}: RouteBuilderPanelProps) {
  const { isDark: themeIsDark } = useTheme();
  const isDark = isDarkProp ?? themeIsDark;
  const [searchOpen, setSearchOpen]             = useState(false);
  const [shareMenuOpen, setShareMenuOpen]       = useState(false);
  const [savedOpen, setSavedOpen]               = useState(false);
  const [tourSettingsOpen, setTourSettingsOpen] = useState(false);
  const isApplePlatform = typeof navigator !== "undefined" && /iPad|iPhone|iPod|Macintosh/.test(navigator.userAgent);

  const waypointIds = new Set(waypoints.map((w) => w.panoId));

  const filteredPanoramas = allPanoramas.filter((f) => {
    const p = f.properties;
    if (p.status !== "active") return false;
    if (!searchQuery.trim()) return true;
    return (p.name ?? "").toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Theme shorthands
  const bg     ="bg-canvas/80 border-ink/10 text-ink";
  const inp    ="bg-ink/5 border-ink/10 text-ink placeholder-tone-500 focus:border-indigo-500";
  const divider ="bg-ink/8";
  const sHead  ="text-tone-500";
  const btnBase ="border-ink/10 text-tone-300 hover:bg-ink/10";

  return (
    <motion.div
      initial={isMobile ? { y: "100%" } : { opacity: 0, x: -10, scale: 0.97 }}
      animate={isMobile ? { y: 0 } : { opacity: 1, x: 0, scale: 1 }}
      exit={isMobile ? { y: "100%" } : { opacity: 0, x: -10, scale: 0.97 }}
      transition={{ type: "spring", stiffness: 340, damping: 30 }}
      className={isMobile
        ? `fixed bottom-0 inset-x-0 z-[46] backdrop-blur-2xl border-t rounded-t-3xl shadow-2xl flex flex-col max-h-[85vh] ${bg}`
        : `w-72 backdrop-blur-2xl border rounded-3xl shadow-2xl flex flex-col max-h-[80vh] ${bg}`}
    >
      {/* ── Drag handle (mobile only) ── */}
      {isMobile && (
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className={`w-8 h-1 rounded-full bg-ink/20`} />
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 shrink-0">
        <MapPin className="w-4 h-4 text-indigo-400 shrink-0" />
        <span className="font-black text-[11px] uppercase tracking-widest flex-1">Route Builder</span>
        <button
          onClick={onClose}
          className={`p-1.5 rounded-full transition-all bg-ink/5 hover:bg-ink/10 text-ink/40 hover:text-ink`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      <div className={`h-px mx-4 ${divider}`} />

      {/* ── Scrollable body ── */}
      <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3 space-y-4">

        {/* Route name */}
        <input
          type="text"
          value={routeName}
          onChange={(e) => onSetRouteName(e.target.value)}
          placeholder="Route name…"
          className={`w-full rounded-xl px-3 py-2 text-sm focus:outline-none border transition-colors ${inp}`}
        />

        {/* Waypoints */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className={`text-[10px] font-black uppercase tracking-widest ${sHead}`}>
              Waypoints ({waypoints.length})
            </p>
            {waypoints.length > 0 && (
              <button
                onClick={() => waypoints.forEach((w) => onRemoveWaypoint(w.panoId))}
                className="text-[10px] text-red-400 hover:text-red-300 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>

          {waypoints.length === 0 ? (
            <div className={`flex flex-col items-center gap-2 py-6 rounded-xl border border-dashed border-ink/10 text-tone-600`}>
              <MapPin className="w-6 h-6 opacity-30" />
              <p className="text-xs">No stops yet — add from map or search</p>
            </div>
          ) : (
            <Reorder.Group
              axis="y"
              values={waypoints}
              onReorder={onReorder}
              className="space-y-1.5"
            >
              {waypoints.map((w, i) => (
                <WaypointRow
                  key={w.panoId}
                  waypoint={w}
                  index={i}
                  onRemove={() => onRemoveWaypoint(w.panoId)}
                  isDark={isDark}
                />
              ))}
            </Reorder.Group>
          )}
        </div>

        {/* Route stats */}
        {waypoints.length >= 2 && (() => {
          if (routeLoading) {
            return (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs ${isDark ? "bg-indigo-500/10 text-indigo-300" : "bg-indigo-50 text-indigo-600"}`}>
                <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin opacity-70" />
                <span className="opacity-60">Calculating route…</span>
              </div>
            );
          }
          if (routeInfo) {
            const km = routeInfo.distanceKm;
            const distLabel = km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
            const mins = Math.round(routeInfo.durationSec / 60);
            const timeLabel = mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60 ? `${mins % 60}m` : ""}`;
            return (
              <div className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs ${isDark ? "bg-indigo-500/10 text-indigo-200" : "bg-indigo-50 text-indigo-700"}`}>
                <Route className="w-3.5 h-3.5 shrink-0 opacity-70" />
                <span className="font-semibold">{distLabel}</span>
                <span className={`mx-1 ${isDark ? "text-indigo-500" : "text-indigo-300"}`}>·</span>
                <Clock className="w-3.5 h-3.5 shrink-0 opacity-70" />
                <span className="font-semibold">{timeLabel}</span>
              </div>
            );
          }
          // Fallback to haversine estimate when API unavailable
          const { distLabel, timeLabel } = routeStats(waypoints);
          return (
            <div className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs ${isDark ? "bg-indigo-500/10 text-indigo-200" : "bg-indigo-50 text-indigo-700"}`}>
              <Route className="w-3.5 h-3.5 shrink-0 opacity-70" />
              <span className="font-semibold">{distLabel}</span>
              <span className={`mx-1 ${isDark ? "text-indigo-500" : "text-indigo-300"}`}>·</span>
              <Clock className="w-3.5 h-3.5 shrink-0 opacity-70" />
              <span className="font-semibold">{timeLabel}</span>
              <span className="ml-auto text-[10px] opacity-50">est.</span>
            </div>
          );
        })()}

        {/* Add buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => { onSetAddingMode(!isAddingMode); setSearchOpen(false); }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border text-[11px] font-bold transition-all ${
              isAddingMode
                ? "bg-indigo-600 border-indigo-500 text-white"
                : `border ${btnBase}`
            }`}
          >
            <Plus className="w-3 h-3" />
            {isAddingMode ? "Click a pin…" : "Click map"}
          </button>
          <button
            onClick={() => { setSearchOpen((v) => !v); onSetAddingMode(false); }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border text-[11px] font-bold transition-all ${
              searchOpen
                ?"bg-ink/10 border-ink/20 text-ink"
                : `border ${btnBase}`
            }`}
          >
            <Search className="w-3 h-3" />
            Search
          </button>
        </div>

        {/* Search dropdown */}
        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <input
                autoFocus
                type="text"
                placeholder="Search panoramas…"
                value={searchQuery}
                onChange={(e) => onSetSearchQuery(e.target.value)}
                className={`w-full rounded-xl px-3 py-2 text-sm focus:outline-none border transition-colors mb-2 ${inp}`}
              />
              <div className={`rounded-xl border overflow-hidden max-h-44 overflow-y-auto border-ink/10`}>
                {filteredPanoramas.length === 0 ? (
                  <p className={`text-xs text-center py-4 text-tone-600`}>No panoramas found</p>
                ) : (
                  filteredPanoramas.slice(0, 30).map((f) => {
                    const p = f.properties;
                    const already = waypointIds.has(p.id);
                    return (
                      <button
                        key={p.id}
                        disabled={already}
                        onClick={() => { onAddWaypoint(f); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 transition-colors text-left ${
                          already
                            ? isDark ? "opacity-40 cursor-default" : "opacity-40 cursor-default"
                            :"hover:bg-ink/10"
                        }`}
                      >
                        <div className={`w-8 h-6 rounded overflow-hidden shrink-0 bg-tone-800`}>
                          {p.thumbnail_url
                            ? <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center"><ImageOff className="w-2.5 h-2.5 text-tone-600" /></div>
                          }
                        </div>
                        <span className={`flex-1 text-xs truncate text-tone-200`}>{p.name ?? "Untitled"}</span>
                        {already
                          ? <Check className="w-3 h-3 text-green-400 shrink-0" />
                          : <Plus className="w-3 h-3 text-indigo-400 shrink-0" />
                        }
                      </button>
                    );
                  })
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={`h-px ${divider}`} />

        {/* Travel mode */}
        <div>
          <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${sHead}`}>Travel Mode</p>
          <div className="grid grid-cols-4 gap-1.5">
            {TRAVEL_MODES.map(({ mode, Icon, label }) => (
              <button
                key={mode}
                onClick={() => onSetTravelMode(mode)}
                className={`flex flex-col items-center gap-1 py-2 rounded-xl border text-[10px] font-bold transition-all ${
                  travelMode === mode
                    ? "bg-indigo-600 border-indigo-500 text-white"
                    : `border ${btnBase}`
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
          {travelMode !== "driving" && (
            <p className={`text-[10px] mt-1.5 text-tone-600`}>
              Waze supports driving only.
            </p>
          )}
        </div>

        <div className={`h-px ${divider}`} />

        {/* Actions */}
        <div className="space-y-2">
          {/* Play Tour Route + Tour Settings */}
          <div className="flex gap-2">
            <button
              onClick={onPlayTourRoute}
              disabled={!onPlayTourRoute}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all disabled:opacity-40 ${
                onPlayTourRoute
                  ? "bg-violet-600 hover:bg-violet-500 text-white"
                  :"bg-ink/5 text-tone-500"
              }`}
            >
              <Play className="w-3.5 h-3.5" />
              Play Tour Route
            </button>
            {tourSettings && onTourSettingsChange && (
              <div className="relative">
                <button
                  onClick={() => setTourSettingsOpen((v) => !v)}
                  title="Tour settings"
                  className={`h-full px-3 rounded-xl border text-xs font-bold transition-all ${
                    tourSettingsOpen
                      ? "bg-indigo-600 border-indigo-500 text-white"
                      :"border-ink/10 text-tone-300 hover:bg-ink/10"
                  }`}
                >
                  <Settings2 className="w-3.5 h-3.5" />
                </button>
                <AnimatePresence>
                  {tourSettingsOpen && (
                    <div className="absolute bottom-full right-0 mb-2 z-50">
                      <TourRouteSettings
                        settings={tourSettings}
                        onSettingsChange={onTourSettingsChange}
                        onClose={() => setTourSettingsOpen(false)}
                        isDark={isDark}
                      />
                    </div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Save as Tour */}
          <button
            onClick={onCreateTour}
            disabled={!onCreateTour || tourCreating}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all disabled:opacity-40 ${
              isDark ? "bg-indigo-600 hover:bg-indigo-500 text-white" : "bg-indigo-600 hover:bg-indigo-500 text-white"
            }`}
          >
            {tourCreating
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <BookmarkPlus className="w-3.5 h-3.5" />
            }
            {tourCreating ? "Saving Tour…" : "Save as Tour"}
          </button>

          {/* Secondary actions row */}
          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={waypoints.length === 0}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border text-[11px] font-bold transition-all disabled:opacity-40 ${
                savedFlash ? "bg-green-600 border-green-500 text-white" : `border ${btnBase}`
              }`}
            >
              {savedFlash ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
              {savedFlash ? "Saved!" : "Save"}
            </button>

            {/* Share dropdown — copy link + open in maps options */}
            <div className="flex-1 relative">
              <button
                onClick={() => setShareMenuOpen((v) => !v)}
                disabled={waypoints.length === 0}
                className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border text-[11px] font-bold transition-all disabled:opacity-40 ${
                  shareCopied ? "bg-green-600 border-green-500 text-white" : `border ${btnBase}`
                }`}
              >
                {shareCopied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                {shareCopied ? "Copied!" : "Share"}
                {!shareCopied && <ChevronDown className={`w-3 h-3 transition-transform ${shareMenuOpen ? "rotate-180" : ""}`} />}
              </button>
              <AnimatePresence>
                {shareMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 4, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 4, scale: 0.97 }}
                    transition={{ type: "spring", stiffness: 400, damping: 28 }}
                    className={`absolute bottom-full mb-1.5 left-0 right-0 rounded-2xl border overflow-hidden shadow-xl z-10 ${
                      isDark ? "bg-neutral-900 border-white/10" : "bg-white border-black/[0.08]"
                    }`}
                  >
                    <button
                      onClick={() => { onCopyShareLink(); setShareMenuOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-xs font-semibold transition-all text-tone-200 hover:bg-ink/10`}
                    >
                      <Link2 className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                      Copy link
                    </button>
                    <div className={`h-px ${divider}`} />
                    <button
                      onClick={() => { onOpenInMaps("google"); setShareMenuOpen(false); }}
                      disabled={waypoints.length < 2}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-xs font-semibold transition-all disabled:opacity-40 text-tone-200 hover:bg-ink/10`}
                    >
                      <Navigation className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      Google Maps
                    </button>
                    <div className={`h-px ${divider}`} />
                    <button
                      onClick={() => { onOpenInMaps("waze"); setShareMenuOpen(false); }}
                      disabled={waypoints.length < 2}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-xs font-semibold transition-all disabled:opacity-40 text-tone-200 hover:bg-ink/10`}
                    >
                      <Navigation className="w-3.5 h-3.5 text-sky-400 shrink-0" />
                      Waze
                    </button>
                    {isApplePlatform && (
                      <>
                        <div className={`h-px ${divider}`} />
                        <button
                          onClick={() => { onOpenInMaps("apple"); setShareMenuOpen(false); }}
                          disabled={waypoints.length < 2}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-xs font-semibold transition-all disabled:opacity-40 text-tone-200 hover:bg-ink/10`}
                        >
                          <Navigation className="w-3.5 h-3.5 text-tone-400 shrink-0" />
                          Apple Maps
                        </button>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={onExportGPX}
              disabled={waypoints.length === 0}
              className={`flex items-center justify-center p-2 rounded-xl border text-[11px] font-bold transition-all disabled:opacity-40 border ${btnBase}`}
              title="Export GPX"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Saved routes */}
        {savedRoutes.length > 0 && (
          <>
            <div className={`h-px ${divider}`} />
            <div>
              <button
                onClick={() => setSavedOpen((v) => !v)}
                className={`w-full flex items-center justify-between text-[10px] font-black uppercase tracking-widest transition-colors ${sHead} hover:text-ink`}
              >
                <span>Saved Routes ({savedRoutes.length})</span>
                {savedOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              <AnimatePresence>
                {savedOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden mt-2 space-y-1.5"
                  >
                    {savedRoutes.map((r) => (
                      <div
                        key={r.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-colors border-ink/5 bg-ink/[0.03] hover:bg-ink/[0.06]`}
                      >
                        <button
                          onClick={() => onLoadRoute(r)}
                          className="flex-1 text-left"
                        >
                          <p className={`text-xs font-semibold truncate text-tone-200`}>{r.name}</p>
                          <p className={`text-[10px] text-tone-600`}>{r.waypoints.length} stop{r.waypoints.length !== 1 ? "s" : ""} · {r.travelMode}</p>
                        </button>
                        <button
                          onClick={() => onDeleteRoute(r.id)}
                          className="p-1 rounded-lg text-tone-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
